import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { flushQueue, subscribeQueueSize } from "@/lib/offlineQueue";
import { queryKeys } from "@/lib/queries";
import { useToast } from "@/components/ui/Toast";

/**
 * Small pill in the TopBar that appears only when the offline mutation
 * queue has pending entries. Clicking it triggers a manual flush (useful
 * if the user knows they're back online but the `online` event didn't
 * fire — e.g., VPN flip, captive portal exit).
 */
export function OfflineQueueBadge() {
  const [size, setSize] = useState(0);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [pending, setPending] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();

  useEffect(() => subscribeQueueSize(setSize), []);

  useEffect(() => {
    function onOnline() {
      setOnline(true);
    }
    function onOffline() {
      setOnline(false);
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (size === 0 && online) return null;

  async function handleFlush() {
    if (!online) {
      toast({ title: "Still offline — will retry when connected", tone: "info" });
      return;
    }
    setPending(true);
    try {
      const result = await flushQueue();
      if (result.sent > 0) {
        void qc.invalidateQueries({ queryKey: queryKeys.app });
      }
      if (result.sent > 0 || result.failed > 0 || result.dropped > 0) {
        toast({
          title: `Flushed ${result.sent} · Retried ${result.failed} · Dropped ${result.dropped}`,
          tone: result.dropped > 0 ? "error" : "success",
        });
      }
    } finally {
      setPending(false);
    }
  }

  const label = !online
    ? size > 0
      ? `Offline · ${size} queued`
      : "Offline"
    : `${size} queued`;
  const tone = !online ? "border-dw-red text-dw-red" : "border-dw-sun text-dw-sun";

  return (
    <button
      type="button"
      onClick={handleFlush}
      disabled={pending || size === 0}
      className={`px-2.5 py-1 text-xs font-body rounded-dw-sm border ${tone} disabled:opacity-50 whitespace-nowrap`}
      title={
        !online
          ? "You're offline. Actions are queued and will send when connectivity returns."
          : "Click to retry pending actions."
      }
    >
      {label}
    </button>
  );
}
